import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import AppLayout from '../../components/common/AppLayout';
import Spinner from '../../components/common/Spinner';
import Modal from '../../components/common/Modal';
import ConfirmModal from '../../components/common/ConfirmModal';
import importApi from '../../services/designProjectImport.api';

// Team role constants
const SALES_TEAM_ROLES = 'director,manager,leader,agent,telemarketer';
const DESIGN_TEAM_ROLES = 'sid,asid,designer,draughter_2d,draughter_site';
const SALES_FIELDS = ['agentName', 'salesLeaderName', 'managerName', 'primaryCloserName', 'cobrokeAgentName'];

// Role display order for dropdowns
const SALES_ROLE_ORDER = ['director', 'manager', 'leader', 'agent', 'telemarketer'];
const DESIGN_ROLE_ORDER = ['sid', 'asid', 'designer', 'draughter_2d', 'draughter_site'];

// Role display names
const ROLE_LABELS = {
  director: 'Director',
  manager: 'Manager',
  leader: 'Leader',
  agent: 'Agent',
  telemarketer: 'Telemarketer',
  sid: 'SID',
  asid: 'ASID',
  designer: 'Designer',
  draughter_2d: 'Draughter 2D',
  draughter_site: 'Draughter Site'
};

// Helper to group users by role
const groupUsersByRole = (users, roleOrder) => {
  const grouped = {};
  roleOrder.forEach(role => {
    grouped[role] = [];
  });

  users.forEach(user => {
    if (grouped[user.role]) {
      grouped[user.role].push(user);
    }
  });

  // Return only roles that have users
  return roleOrder.filter(role => grouped[role].length > 0).map(role => ({
    role,
    label: ROLE_LABELS[role] || role,
    users: grouped[role]
  }));
};

/**
 * NameFieldSection - Renders a field type section with expandable name groups
 */
function NameFieldSection({
  fieldType,
  fieldData,
  model,
  expandedNames,
  toggleNameExpand,
  selectedMappings,
  updateMapping,
  handleApplyMapping,
  loadUsersForRoles,
  getRolesForField,
  usersCache,
  applyingMapping
}) {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showAllTeam, setShowAllTeam] = useState(false);

  // Determine if this is a sales or design field
  const isSalesField = SALES_FIELDS.includes(fieldType);
  const roleOrder = isSalesField ? SALES_ROLE_ORDER : DESIGN_ROLE_ORDER;

  // Get effective roles based on toggle state
  const getEffectiveRoles = () => {
    if (showAllTeam) {
      return isSalesField ? SALES_TEAM_ROLES : DESIGN_TEAM_ROLES;
    }
    return getRolesForField(fieldType);
  };

  // Group users by role for organized dropdown
  const groupedUsers = groupUsersByRole(users, roleOrder);

  // Load users when component mounts or toggle changes
  useEffect(() => {
    const roles = getEffectiveRoles();
    if (roles && usersCache[roles]) {
      setUsers(usersCache[roles]);
    } else if (roles) {
      setLoadingUsers(true);
      loadUsersForRoles(roles).then(loadedUsers => {
        setUsers(loadedUsers);
        setLoadingUsers(false);
      });
    }
  }, [fieldType, showAllTeam, getRolesForField, loadUsersForRoles, usersCache]);

  const totalRecords = fieldData.groups.reduce((sum, g) => sum + g.count, 0);

  return (
    <div className="p-4">
      {/* Field Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h5 className="font-medium text-gray-900">{fieldData.label}</h5>
          <p className="text-sm text-gray-500">{totalRecords} unmapped record{totalRecords !== 1 ? 's' : ''}</p>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer hover:text-gray-900">
          <input
            type="checkbox"
            checked={showAllTeam}
            onChange={(e) => setShowAllTeam(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Show all {isSalesField ? 'Sales' : 'Design'} team
        </label>
      </div>

      {/* Name Groups */}
      <div className="space-y-2">
        {fieldData.groups.map(group => {
          const expandKey = `${fieldType}-${group.name}`;
          const isExpanded = expandedNames[expandKey];
          const allKey = `${fieldType}-${group.name}-all`;

          return (
            <div key={group.name} className="border border-gray-200 rounded-lg overflow-hidden">
              {/* Group Header */}
              <div
                className="flex items-center justify-between p-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                onClick={() => toggleNameExpand(fieldType, group.name)}
              >
                <div className="flex items-center gap-3">
                  <svg
                    className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <span className="font-medium text-gray-900">"{group.name}"</span>
                  <span className="text-sm text-gray-500">({group.count} record{group.count !== 1 ? 's' : ''})</span>
                </div>

                {/* Bulk mapping dropdown */}
                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                  <select
                    value={selectedMappings[allKey] || ''}
                    onChange={e => updateMapping(fieldType, group.name, null, e.target.value)}
                    className="text-sm border border-gray-300 rounded px-2 py-1 min-w-[180px]"
                    disabled={loadingUsers}
                  >
                    <option value="">Select user...</option>
                    {groupedUsers.map(group => (
                      <optgroup key={group.role} label={group.label}>
                        {group.users.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.fullName || user.nickname}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  <button
                    onClick={() => handleApplyMapping(fieldType, model, group.name)}
                    disabled={!selectedMappings[allKey] || applyingMapping}
                    className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Apply All
                  </button>
                </div>
              </div>

              {/* Expanded Records */}
              {isExpanded && (
                <div className="border-t border-gray-200 divide-y divide-gray-100">
                  {group.records.map(record => {
                    const recordId = record.salesFormId || record.designProjectId;
                    const recordKey = `${fieldType}-${recordId}`;

                    return (
                      <div key={recordId} className="flex items-center justify-between p-3 pl-10 bg-white">
                        <div className="text-sm">
                          <span className="font-medium text-gray-700">{record.projectNumber}</span>
                          <span className="text-gray-400 mx-2">-</span>
                          <span className="text-gray-600">{record.clientName}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <select
                            value={selectedMappings[recordKey] || selectedMappings[allKey] || ''}
                            onChange={e => updateMapping(fieldType, group.name, recordId, e.target.value)}
                            className="text-sm border border-gray-300 rounded px-2 py-1 min-w-[180px]"
                            disabled={loadingUsers}
                          >
                            <option value="">Select user...</option>
                            {groupedUsers.map(grp => (
                              <optgroup key={grp.role} label={grp.label}>
                                {grp.users.map(user => (
                                  <option key={user.id} value={user.id}>
                                    {user.fullName || user.nickname}
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                          <button
                            onClick={() => handleApplyMapping(fieldType, model, group.name, recordId)}
                            disabled={!(selectedMappings[recordKey] || selectedMappings[allKey]) || applyingMapping}
                            className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Map
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DesignProjectImportPage() {
  // Tab state
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' | 'sessions' | 'mapping'

  // Upload state
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  // Sessions state
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetailOpen, setSessionDetailOpen] = useState(false);

  // Rollback state
  const [rollbackModalOpen, setRollbackModalOpen] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);
  const [sessionToRollback, setSessionToRollback] = useState(null);

  // Name mapping state
  const [unmappedData, setUnmappedData] = useState(null);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [usersCache, setUsersCache] = useState({});
  const [expandedNames, setExpandedNames] = useState({});
  const [selectedMappings, setSelectedMappings] = useState({});
  const [applyingMapping, setApplyingMapping] = useState(false);

  // Load sessions on mount and tab change
  useEffect(() => {
    if (activeTab === 'sessions') {
      loadSessions();
    } else if (activeTab === 'mapping') {
      loadUnmappedNames();
    }
  }, [activeTab]);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const response = await importApi.listSessions({ limit: 50 });
      setSessions(response.data.sessions);
    } catch (error) {
      toast.error('Failed to load import sessions');
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  // Load unmapped names for mapping tab
  const loadUnmappedNames = useCallback(async () => {
    setMappingLoading(true);
    try {
      const response = await importApi.getUnmappedNames();
      setUnmappedData(response.data);
    } catch (error) {
      toast.error('Failed to load unmapped names');
    } finally {
      setMappingLoading(false);
    }
  }, []);

  // Load users for a specific role set (cached)
  const loadUsersForRoles = async (roles) => {
    const cacheKey = roles;
    if (usersCache[cacheKey]) {
      return usersCache[cacheKey];
    }
    try {
      const response = await importApi.getUsersForMapping(roles);
      const users = response.data.users;
      setUsersCache(prev => ({ ...prev, [cacheKey]: users }));
      return users;
    } catch (error) {
      console.error('Failed to load users:', error);
      return [];
    }
  };

  // Get role filter for each field type
  const getRolesForField = (fieldType) => {
    const roleMap = {
      agentName: 'agent,telemarketer',
      salesLeaderName: 'leader',
      managerName: 'manager',
      primaryCloserName: 'agent,telemarketer',
      cobrokeAgentName: 'agent,telemarketer',
      sidName: 'sid',
      asidName: 'asid',
      designerName: 'designer',
      draughter2dName: 'draughter_2d'
    };
    return roleMap[fieldType] || '';
  };

  // Toggle name group expansion
  const toggleNameExpand = (fieldType, name) => {
    const key = `${fieldType}-${name}`;
    setExpandedNames(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Update mapping selection
  const updateMapping = (fieldType, name, recordId, userId) => {
    const key = recordId ? `${fieldType}-${recordId}` : `${fieldType}-${name}-all`;
    setSelectedMappings(prev => ({ ...prev, [key]: userId }));
  };

  // Apply mapping for a single record or all records with a name
  const handleApplyMapping = async (fieldType, model, name, recordId = null) => {
    const key = recordId ? `${fieldType}-${recordId}` : `${fieldType}-${name}-all`;
    const userId = selectedMappings[key];

    if (!userId) {
      toast.error('Please select a user first');
      return;
    }

    setApplyingMapping(true);
    try {
      const mapping = {
        fieldType,
        model,
        userId,
        name,
        recordIds: recordId ? [recordId] : ['all']
      };
      await importApi.applyNameMapping([mapping]);
      toast.success('Mapping applied successfully');
      loadUnmappedNames(); // Refresh the list
    } catch (error) {
      toast.error('Failed to apply mapping');
    } finally {
      setApplyingMapping(false);
    }
  };

  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setFile(selectedFile);
      setValidationResult(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (!droppedFile.name.endsWith('.csv')) {
        toast.error('Please drop a CSV file');
        return;
      }
      setFile(droppedFile);
      setValidationResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    try {
      const response = await importApi.uploadAndValidate(file);
      setValidationResult(response.data);

      if (response.data.valid) {
        toast.success('File validated successfully. Review and confirm to import.');
      } else {
        toast.warning(`Found ${response.data.errorCount} validation errors. Please review.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!validationResult?.sessionId) {
      toast.error('No valid session to import');
      return;
    }

    setImporting(true);
    try {
      const response = await importApi.confirmImport(validationResult.sessionId);
      toast.success(`Successfully imported ${response.data.successCount} records`);

      // Reset state
      setFile(null);
      setValidationResult(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Switch to sessions tab to see result
      setActiveTab('sessions');
      loadSessions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await importApi.downloadTemplate();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'design_project_import_template.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleViewSession = async (session) => {
    setSelectedSession(session);
    setSessionDetailOpen(true);
  };

  const handleRollbackClick = (session) => {
    setSessionToRollback(session);
    setRollbackModalOpen(true);
  };

  const handleConfirmRollback = async () => {
    if (!sessionToRollback) return;

    setRollingBack(true);
    try {
      const response = await importApi.rollbackSession(sessionToRollback.id);
      toast.success(`Rolled back ${response.data.deletedCount} records`);
      setRollbackModalOpen(false);
      loadSessions();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Rollback failed');
    } finally {
      setRollingBack(false);
    }
  };

  const handleCancelUpload = () => {
    setFile(null);
    setValidationResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      rolled_back: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`inline-block px-2 py-1 text-xs font-medium rounded ${styles[status] || 'bg-gray-100'}`}>
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-6 max-w-full">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-display font-bold text-tenq-black">Design Project Import</h1>
          <p className="text-gray-600 mt-1">Import design projects from CSV files</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-200">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('upload')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'upload'
                  ? 'border-tenq-gold text-tenq-gold'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Upload CSV
            </button>
            <button
              onClick={() => setActiveTab('sessions')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'sessions'
                  ? 'border-tenq-gold text-tenq-gold'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Import History
            </button>
            <button
              onClick={() => setActiveTab('mapping')}
              className={`pb-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                activeTab === 'mapping'
                  ? 'border-tenq-gold text-tenq-gold'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Name Mapping
              {unmappedData?.summary?.totalUnmapped > 0 && (
                <span className="bg-orange-100 text-orange-700 text-xs font-medium px-2 py-0.5 rounded-full">
                  {unmappedData.summary.totalUnmapped}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="space-y-6">
            {/* Template Download */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-blue-800">CSV Template</h3>
                  <p className="text-sm text-blue-600 mt-1">
                    Download the template to see all required fields and format
                  </p>
                </div>
                <button
                  onClick={handleDownloadTemplate}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
                >
                  Download Template
                </button>
              </div>
            </div>

            {/* File Upload */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="font-medium text-gray-900 mb-4">Upload CSV File</h3>

              <div
                onDragOver={handleDragOver}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragging
                    ? 'border-tenq-gold bg-tenq-gold/5'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="csv-upload"
                />
                <label
                  htmlFor="csv-upload"
                  className="cursor-pointer block"
                >
                  <div className={`mb-4 ${isDragging ? 'text-tenq-gold' : 'text-gray-400'}`}>
                    <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                  </div>
                  <p className={`mb-2 ${isDragging ? 'text-tenq-gold font-medium' : 'text-gray-600'}`}>
                    {isDragging ? 'Drop your CSV file here' : file ? file.name : 'Click to select a CSV file'}
                  </p>
                  <p className="text-sm text-gray-400">
                    {file ? `${(file.size / 1024).toFixed(1)} KB` : 'or drag and drop'}
                  </p>
                </label>
              </div>

              {file && !validationResult && (
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={handleUpload}
                    disabled={uploading}
                    className="px-6 py-2 bg-tenq-gold text-tenq-black font-semibold rounded-lg hover:bg-tenq-gold/90 disabled:opacity-50"
                  >
                    {uploading ? 'Validating...' : 'Validate File'}
                  </button>
                  <button
                    onClick={handleCancelUpload}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            {/* Validation Results */}
            {validationResult && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="font-medium text-gray-900 mb-4">Validation Results</h3>

                {/* Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900">{validationResult.totalRows}</div>
                    <div className="text-sm text-gray-600">Total Rows</div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{validationResult.validRows}</div>
                    <div className="text-sm text-gray-600">Valid Rows</div>
                  </div>
                  <div className="bg-red-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-red-600">{validationResult.errorCount}</div>
                    <div className="text-sm text-gray-600">Errors</div>
                  </div>
                  <div className="bg-yellow-50 rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold text-yellow-600">{validationResult.warningCount || 0}</div>
                    <div className="text-sm text-gray-600">Warnings</div>
                  </div>
                </div>

                {/* Errors List */}
                {validationResult.errors?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium text-red-700 mb-2">Errors (must fix before import)</h4>
                    <div className="max-h-60 overflow-y-auto border border-red-200 rounded-lg">
                      <table className="w-full text-sm">
                        <thead className="bg-red-50 sticky top-0">
                          <tr>
                            <th className="px-4 py-2 text-left text-red-700">Row</th>
                            <th className="px-4 py-2 text-left text-red-700">Field</th>
                            <th className="px-4 py-2 text-left text-red-700">Error</th>
                            <th className="px-4 py-2 text-left text-red-700">Value</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-red-100">
                          {validationResult.errors.slice(0, 100).map((err, idx) => (
                            <tr key={idx}>
                              <td className="px-4 py-2 text-gray-900">{err.row}</td>
                              <td className="px-4 py-2 text-gray-700">{err.field}</td>
                              <td className="px-4 py-2 text-red-600">{err.error}</td>
                              <td className="px-4 py-2 text-gray-500 truncate max-w-xs">
                                {err.value !== null ? String(err.value) : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {validationResult.errors.length > 100 && (
                        <div className="p-2 text-center text-sm text-gray-500 bg-red-50">
                          Showing first 100 of {validationResult.errors.length} errors
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Preview Data */}
                {validationResult.preview?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-medium text-gray-700 mb-2">Data Preview (first 10 rows)</h4>
                    <p className="text-xs text-gray-500 mb-2">Scroll horizontally to see all {Object.keys(validationResult.preview[0] || {}).length} columns</p>
                    <div className="overflow-x-auto border border-gray-200 rounded-lg max-h-80">
                      <table className="text-sm">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            {Object.keys(validationResult.preview[0] || {}).filter(k => k !== '_rowNumber').map(key => (
                              <th key={key} className="px-3 py-2 text-left text-gray-700 whitespace-nowrap text-xs font-medium border-b border-gray-200">
                                {key}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {validationResult.preview.slice(0, 10).map((row, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              {Object.entries(row).filter(([k]) => k !== '_rowNumber').map(([key, val], i) => (
                                <td key={i} className="px-3 py-2 text-gray-900 whitespace-nowrap text-xs max-w-[200px] truncate" title={val !== null ? String(val) : ''}>
                                  {val !== null && val !== '' ? String(val) : <span className="text-gray-300">-</span>}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  {validationResult.valid && (
                    <button
                      onClick={handleConfirmImport}
                      disabled={importing}
                      className="px-6 py-2 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50"
                    >
                      {importing ? 'Importing...' : `Confirm Import (${validationResult.validRows} records)`}
                    </button>
                  )}
                  <button
                    onClick={handleCancelUpload}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    {validationResult.valid ? 'Cancel' : 'Upload Different File'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sessions Tab */}
        {activeTab === 'sessions' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            {sessionsLoading ? (
              <div className="p-8 flex justify-center">
                <Spinner size="lg" />
              </div>
            ) : sessions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No import sessions found
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">File</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Success</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Errors</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Imported By</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {sessions.map(session => (
                      <tr key={session.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {new Date(session.createdAt).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {session.fileName || '-'}
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(session.status)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {session.totalRows}
                        </td>
                        <td className="px-4 py-3 text-sm text-green-600 font-medium">
                          {session.successCount}
                        </td>
                        <td className="px-4 py-3 text-sm text-red-600 font-medium">
                          {session.errorCount}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {session.importer?.fullName || session.importer?.nickname || '-'}
                        </td>
                        <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                          <button
                            onClick={() => handleViewSession(session)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            View
                          </button>
                          {session.status === 'completed' && (
                            <button
                              onClick={() => handleRollbackClick(session)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              Rollback
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Name Mapping Tab */}
        {activeTab === 'mapping' && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-900">Name Mapping</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Map imported names to user accounts. Names are grouped for bulk mapping.
                  </p>
                </div>
                <button
                  onClick={loadUnmappedNames}
                  disabled={mappingLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  {mappingLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {unmappedData?.summary && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <p className="text-orange-800 font-medium">
                    {unmappedData.summary.totalUnmapped} unmapped records across {Object.keys(unmappedData.summary.byField).length} fields
                  </p>
                </div>
              )}
            </div>

            {mappingLoading ? (
              <div className="flex justify-center py-12">
                <Spinner size="lg" />
              </div>
            ) : unmappedData?.summary?.totalUnmapped === 0 ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
                <svg className="mx-auto h-12 w-12 text-green-500 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-lg font-medium text-green-800">All names are mapped</h3>
                <p className="text-green-600 mt-1">No unmapped names found</p>
              </div>
            ) : (
              <>
                {/* Sales Team Section */}
                {Object.keys(unmappedData?.salesFormFields || {}).length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="bg-blue-50 px-6 py-3 border-b border-blue-100">
                      <h4 className="font-semibold text-blue-900">Sales Team</h4>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {Object.entries(unmappedData.salesFormFields).map(([fieldType, fieldData]) => (
                        <NameFieldSection
                          key={fieldType}
                          fieldType={fieldType}
                          fieldData={fieldData}
                          model="salesForm"
                          expandedNames={expandedNames}
                          toggleNameExpand={toggleNameExpand}
                          selectedMappings={selectedMappings}
                          updateMapping={updateMapping}
                          handleApplyMapping={handleApplyMapping}
                          loadUsersForRoles={loadUsersForRoles}
                          getRolesForField={getRolesForField}
                          usersCache={usersCache}
                          applyingMapping={applyingMapping}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Design Team Section */}
                {Object.keys(unmappedData?.designProjectFields || {}).length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="bg-purple-50 px-6 py-3 border-b border-purple-100">
                      <h4 className="font-semibold text-purple-900">Design Team</h4>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {Object.entries(unmappedData.designProjectFields).map(([fieldType, fieldData]) => (
                        <NameFieldSection
                          key={fieldType}
                          fieldType={fieldType}
                          fieldData={fieldData}
                          model="designProject"
                          expandedNames={expandedNames}
                          toggleNameExpand={toggleNameExpand}
                          selectedMappings={selectedMappings}
                          updateMapping={updateMapping}
                          handleApplyMapping={handleApplyMapping}
                          loadUsersForRoles={loadUsersForRoles}
                          getRolesForField={getRolesForField}
                          usersCache={usersCache}
                          applyingMapping={applyingMapping}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Session Detail Modal */}
        <Modal
          isOpen={sessionDetailOpen}
          onClose={() => setSessionDetailOpen(false)}
          title="Import Session Details"
          size="lg"
        >
          {selectedSession && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">Status</label>
                  <div className="mt-1">{getStatusBadge(selectedSession.status)}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">File Name</label>
                  <div className="mt-1 text-gray-900">{selectedSession.fileName || '-'}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Created</label>
                  <div className="mt-1 text-gray-900">
                    {new Date(selectedSession.createdAt).toLocaleString()}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Completed</label>
                  <div className="mt-1 text-gray-900">
                    {selectedSession.completedAt
                      ? new Date(selectedSession.completedAt).toLocaleString()
                      : '-'}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Total Rows</label>
                  <div className="mt-1 text-gray-900">{selectedSession.totalRows}</div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-500">Success / Errors</label>
                  <div className="mt-1">
                    <span className="text-green-600 font-medium">{selectedSession.successCount}</span>
                    <span className="text-gray-400 mx-2">/</span>
                    <span className="text-red-600 font-medium">{selectedSession.errorCount}</span>
                  </div>
                </div>
              </div>

              {/* Validation Errors */}
              {selectedSession.validationErrors?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">Validation Errors</label>
                  <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg text-sm">
                    <table className="w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-2 text-left">Row</th>
                          <th className="px-3 py-2 text-left">Field</th>
                          <th className="px-3 py-2 text-left">Error</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {selectedSession.validationErrors.slice(0, 50).map((err, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2">{err.row}</td>
                            <td className="px-3 py-2">{err.field}</td>
                            <td className="px-3 py-2 text-red-600">{err.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Imported Records Summary */}
              {selectedSession.importedRecords?.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-2">
                    Imported Records ({selectedSession.importedRecords.length})
                  </label>
                  <p className="text-sm text-gray-600">
                    Records have been created in Lead, SalesForm, and DesignProject tables.
                  </p>
                </div>
              )}

              {/* Rollback Info */}
              {selectedSession.status === 'rolled_back' && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Rolled back:</span>{' '}
                    {selectedSession.rolledBackAt
                      ? new Date(selectedSession.rolledBackAt).toLocaleString()
                      : '-'}
                  </p>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* Rollback Confirmation */}
        <ConfirmModal
          isOpen={rollbackModalOpen}
          onClose={() => setRollbackModalOpen(false)}
          onConfirm={handleConfirmRollback}
          title="Rollback Import"
          message={
            <div>
              <p className="mb-4">
                Are you sure you want to rollback this import? This will delete all{' '}
                <strong>{sessionToRollback?.successCount || 0}</strong> records that were created.
              </p>
              <p className="text-sm text-gray-500">
                This action will delete the Lead, SalesForm, and DesignProject records created by this import.
              </p>
            </div>
          }
          confirmText={rollingBack ? 'Rolling back...' : 'Rollback'}
          confirmVariant="danger"
        />
      </div>
    </AppLayout>
  );
}

export default DesignProjectImportPage;
